
export async function generateWordReport(patient, htmlContent, reportStatus = "open") {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    AlignmentType, BorderStyle, WidthType, ShadingType
  } = await import("docx");
  const { saveAs } = await import("file-saver");
  const TEXT_DARK = "333333";
 
  const fields = [
    ["Patient Name",    patient.patientName        || patient.patient_name        || "N/A"],
    ["Patient ID",      patient.patientId          || patient.patient_id          || "N/A"],
    ["Age / Gender",    `${patient.patient_age || "N/A"} / ${patient.patient_gender || "N/A"}`],
    ["Study Date",      patient.studyDate          || patient.study_date          || "N/A"],
    ["Modality",        patient.modality                                          || "N/A"],
    ["Ref. Physician",  patient.referringPhysician || patient.referring_physician || "N/A"],
  ];
  const detailRows = [];
  const makeCell = (label, value, isHeader = false) => new TableCell({
    shading: isHeader ? {  type: ShadingType.CLEAR } : undefined,
    borders: {
      top:    { style: BorderStyle.SINGLE, size: 4 },
      bottom: { style: BorderStyle.SINGLE, size: 4 },
      left:   { style: BorderStyle.SINGLE, size: 4 },
      right:  { style: BorderStyle.SINGLE, size: 4 },
    },
    width: { size: 2340, type: WidthType.DXA }, // Balanced 4-column widths
    margins: { top: 100, bottom: 100, left: 140, right: 140 },
    children: [
      new Paragraph({
        children: [
          new TextRun({ 
            text: value ? label : label, 
            bold: isHeader, 
            size: 20, 
            font: "Calibri", 
            // color: isHeader ? BLUE : TEXT_DARK 
          }),
          ...(!isHeader && value ? [
            new TextRun({ text: value, size: 20, font: "Calibri", color: TEXT_DARK })
          ] : [])
        ],
      }),
    ],
  });

  for (let i = 0; i < fields.length; i += 2) {
    const left = fields[i];
    const right = fields[i + 1] || ["", ""];

    detailRows.push(
      new TableRow({
        children: [
          makeCell(left[0] + ": ", null, true),  // Label 1 (shaded)
          makeCell(left[1], null, false),        // Value 1
          makeCell(right[0] ? right[0] + ": " : "", null, true), // Label 2 (shaded)
          makeCell(right[1], null, false),       // Value 2
        ]
      })
    );
  }
  const { ImageRun } = await import("docx");
  
  let signatureChild;
  const imageUrl = `${API_BASE_URL}/${patient.signature}`;
  if (imageUrl) {
    const imageBuffer = await fetch(imageUrl).then((res) =>
      res.arrayBuffer()
    );
  
    signatureChild = new ImageRun({
      data: imageBuffer,
      transformation: {
        width: 180,
        height: 60,
      },
    });
  } else {
    signatureChild = new TextRun({
      text: "______________________________",
      size: 20,
    });
  }
  // ── Process HTML Content to docx Paragraphs ─────────────────────────────────
  const tmp = document.createElement("div");
  tmp.innerHTML = htmlContent || "";
  const paras = [];
  tmp.querySelectorAll("p,h1,h2,h3,li,div").forEach(el => {
    const tag  = el.tagName.toLowerCase();
    const text = el.textContent || " ";
    paras.push(new Paragraph({
      children: [new TextRun({
        text,
        bold: ["h1","h2","h3"].includes(tag),
        size: tag==="h1"?28:tag==="h2"?26:tag==="h3"?24:22,
        font: "Calibri",
      })],
      spacing: { after: 120 },
    }));
  });
  if (!paras.length) {
    (htmlContent||"").split("\n").forEach(line =>
      paras.push(new Paragraph({ children: [new TextRun({ text: line||" ", size: 22, font: "Calibri" })], spacing: { after: 120 } }))
    );
  }
  
  const doc = new Document({
    styles: { default: { document: { run: { font: "Calibri", size: 22 } } } },
    sections: [{
      properties: {
        page: {
          size:   { width: 12240, height: 15840 },
          margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 },
        },
      },
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 60 },
          children: [
            new TextRun({ text: `${patient.center_name}`, bold: true, size: 36, font: "Calibri" }),
          ],
        }),
        
        new Paragraph({
          spacing: { before: 240, after: 160 },
          children: [new TextRun({ text: "PATIENT DETAILS", bold: true, size: 24, font: "Calibri"})],
        }),
        
        // Rendered Grid Table
        new Table({ width: { size: 9360, type: WidthType.DXA }, rows: detailRows }),
        
        new Paragraph({
          spacing: { before: 400, after: 160 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 4,  space: 1 } },
          children: [new TextRun({ text: "RADIOLOGICAL FINDINGS & REPORT", bold: true, size: 24, font: "Calibri", })],
        }),
        ...paras,
        new Paragraph({
          spacing: { before: 600, after: 80 },
          border: { top: { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC", space: 1 } },
          children: [new TextRun({ text: " " })],
        }),
        
        new Paragraph({
          children: [signatureChild],
          spacing: {
            after: 60,
          },
        }),
        new Paragraph({
          children: [
            new TextRun({ 
              text: patient ? `${patient.doctor_name} (${patient.designation || "Consultant Radiologist"})` : "________________________________", 
              size: 20, 
              font: "Calibri" 
            })
          ],
          spacing: {
            after: 60,
          },
        }),
        new Paragraph({ children: [new TextRun({ text: `Report Generated: ${new Date().toLocaleString("en-IN")}`, size: 18, font: "Calibri", color: "888888", italics: true })] }),
      ],
    }],
  });
  
  const blob     = await Packer.toBlob(doc);
  const safeName = (patient.patientName || patient.patient_name || "Patient").replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_]/g,"");
  saveAs(blob, `Radiology_Report_${safeName}_${Date.now()}.docx`);
}